import type { AnyRoute } from "@tanstack/react-router";
import type React from "react";

import { NoLogoIcon } from "@/components/Icon/ForceIcons";
import * as ProductLogos from "@/components/Logo/Product";

// ルートを加工する関数群
// 2025-07-01時点では、CommandPalette用に暫定で作成しています。
// productionリリースに必要なカイゼン：
// - SidebarConfig.tsxと統合
// - ProductAccessCheckMapを使って、ユーザーがアクセス可能なルートを取得
// - 動的なルートを扱えるように（Appsとかは特に！）。
// - 検索性の向上。例えば、日本語検索に対応。ルートメタ情報を整備。

export type AppRoute = {
  path: string; // Route path (URL)
  label: string; // Display name for the route
  icon?: React.ComponentType<{ className?: string }>; // Optional icon name (from Icon component)
  keywords?: string[]; // Optional keywords for search
  searchableText: string; // Pre-computed text for efficient filtering
};

// Helper: Generate a label from a path segment
function pathToLabel(path: string): string {
  // Remove leading/trailing slashes, split by '/', capitalize each segment
  return (
    path
      .replace(/^\/+|\/+$/g, "")
      .split("/")
      .filter(Boolean)
      .map((seg) =>
        seg.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      )
      .join(" / ") || "Root"
  );
}

// Generate keywords from a path string.
// e.g., "/people/employees/list" -> ["people", "employees", "list"]
// e.g., "/it-operation-core" -> ["it", "operation", "core"]
function pathToKeywords(path: string): string[] {
  return path
    .replace(/^\/+|\/+$/g, "") // Remove leading/trailing slashes
    .split("/") // Split by path segments
    .flatMap((seg) => seg.split("-")) // Split by hyphens within segments
    .filter(Boolean); // Remove any empty strings
}

// A mapping for special cases where the path name does not directly correspond to a logo name.
// Keys should be ordered from most specific to least specific.
const specialCaseLogoMap: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  "/settings/permission-and-roles":
    ProductLogos.ProductLogoSmallPermissionAndRoles,
  "/settings/persons": ProductLogos.ProductLogoSmallUserAccounts,
  "/ids": ProductLogos.ProductLogoSmallIDs,
};

// Converts a path segment (e.g., "it-operation-core") to a PascalCase string (e.g., "ITOperationCore").
// Handles special acronyms like "IT" and "ID".
function pathToPascalCase(segment: string): string {
  return segment
    .split("-")
    .map((word) => {
      if (word.toLowerCase() === "it" || word.toLowerCase() === "id") {
        return word.toUpperCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join("");
}

// Assign a logo component based on route path heuristics.
// It first checks for special cases, then attempts to dynamically match the path to a logo component.
function assignLogo(path: string): React.ComponentType<{ className?: string }> {
  // Find the first matching prefix in our special case map.
  // The map is ordered, so this will find the most specific match.
  const matchingPrefix = Object.keys(specialCaseLogoMap).find((prefix) =>
    path.startsWith(prefix),
  );

  if (matchingPrefix) {
    return specialCaseLogoMap[matchingPrefix];
  }

  // Dynamic assignment for common cases.
  const pathSegment = path.split("/").filter(Boolean)[0];
  if (pathSegment) {
    const pascalCaseName = pathToPascalCase(pathSegment);
    const logoName =
      `ProductLogoSmall${pascalCaseName}` as keyof typeof ProductLogos;
    if (logoName in ProductLogos) {
      return ProductLogos[logoName];
    }
  }

  // Default fallback icon.
  return NoLogoIcon;
}

// Generate AppRoute[] from flatRoutes (from useRouter)
export function getStaffAppRoutes(flatRoutes: AnyRoute[]): AppRoute[] {
  if (!Array.isArray(flatRoutes)) return [];

  const appRoutes: AppRoute[] = [];
  const seenPaths = new Set<string>();

  for (const route of flatRoutes) {
    // Normalize path: remove trailing slash for consistency, unless it's the root path.
    let path = route.fullPath;
    if (path !== "/" && path.endsWith("/")) {
      path = path.slice(0, -1);
    }

    // Conditions for inclusion:
    // 1. Staff-related route (from file path `id`).
    // 2. Has a non-empty, string path.
    // 3. Not a dynamic route (no '$' or ':').
    // 4. Not a layout route file.
    // 5. Not a duplicate path.
    if (
      route.id.startsWith("/_staff") &&
      typeof path === "string" &&
      path &&
      !path.includes("$") &&
      !path.includes(":") &&
      !route.id.includes("_layout") &&
      !route.id.endsWith("/_layout") && // Also exclude things like /_staff/apps/_layout
      !seenPaths.has(path)
    ) {
      // Exclude root of staff section, which is just a layout.
      if (route.id === "/_staff") continue;

      const label = pathToLabel(path);
      const keywords = pathToKeywords(path);
      const searchableItems = [label, path, ...(keywords || [])];

      appRoutes.push({
        path,
        label,
        icon: assignLogo(path),
        keywords,
        searchableText: searchableItems.flat().join(" ").toLowerCase(),
      });
      seenPaths.add(path);
    }
  }

  // Sort alphabetically for a consistent and predictable order.
  appRoutes.sort((a, b) => a.label.localeCompare(b.label));

  return appRoutes;
}

// Filter AppRoute[] by search string (label, path, keywords).
// Supports multi-word search (e.g., "user settings").
const SPLIT_REGEX = /\s+/;

export function filterAppRoutes(
  routes: AppRoute[],
  search: string,
): AppRoute[] {
  if (!search) return routes;

  const searchTerms = search.toLowerCase().split(SPLIT_REGEX).filter(Boolean);

  if (searchTerms.length === 0) return routes;

  return routes.filter((route) =>
    // Use the pre-computed searchable text for efficiency.
    searchTerms.every((term) => route.searchableText.includes(term)),
  );
}
